import type { NavigationItem } from '@/utils/navigationTypes';
import { ModuleNavLabel } from '@/components/navigation/ModuleNavLabel';
import type { ModuleDefinition } from '@/modules/types';
import { isAdminEquivalentRole } from '@/utils/authz';

// Re-export createNavItem for convenience
export { createNavItem } from './createNavItem';

/** Slot key where module navigation items are injected (manage context) */
export const MODULE_NAV_SLOT_KEY = 'external-modules' as const;

/** Filter items by requiredTenantRoles; ADMIN bypasses all checks */
function filterItemsByTenantRoles(
  items: NavigationItem[],
  tenantRoles: string[],
  isAdmin: boolean
): NavigationItem[] {
  if (isAdmin) return items;
  return items
    .map((item) => {
      if (item.type === 'group' && item.children) {
        const filteredChildren = filterItemsByTenantRoles(item.children, tenantRoles, false);
        return { ...item, children: filteredChildren };
      }
      if (item.requiredTenantRoles && item.requiredTenantRoles.length > 0) {
        const hasRole = item.requiredTenantRoles.some((r) => tenantRoles.includes(r));
        if (!hasRole) return null;
      }
      return item;
    })
    .filter((item): item is NavigationItem => item !== null)
    .map((item) => {
      if (item.type === 'group' && item.children?.length === 0 && item.key !== MODULE_NAV_SLOT_KEY) return null;
      return item;
    })
    .filter((item): item is NavigationItem => item !== null);
}

/** Check if module is accessible given user and tenant roles */
function moduleMatchesRoles(
  module: ModuleDefinition,
  userRoles: string[],
  tenantRoles: string[],
  isAdmin: boolean
): boolean {
  if (isAdmin) return true;
  if (module.requiredRoles && module.requiredRoles.length > 0) {
    if (!module.requiredRoles.some((r) => userRoles.includes(r))) return false;
  }
  if (module.requiredTenantRoles && module.requiredTenantRoles.length > 0) {
    if (!module.requiredTenantRoles.some((r) => tenantRoles.includes(r))) return false;
  }
  return true;
}

/**
 * Build navigation items by filtering static items by tenant roles,
 * filtering enabled modules by user/tenant roles, and injecting
 * module items into the module slot.
 */
export function buildNavigationItems(
  staticItems: NavigationItem[],
  options: {
    enabledModules: ModuleDefinition[];
    userRoles: string[];
    tenantRoles: string[];
    moduleSlotKey?: string;
  }
): NavigationItem[] {
  const { enabledModules, userRoles, tenantRoles, moduleSlotKey = MODULE_NAV_SLOT_KEY } = options;
  const isAdmin = userRoles.some((role) => isAdminEquivalentRole(role));

  const filteredStatic = filterItemsByTenantRoles(staticItems, tenantRoles, isAdmin);

  const moduleNavItems: NavigationItem[] = [];
  for (const module of enabledModules) {
    if (!moduleMatchesRoles(module, userRoles, tenantRoles, isAdmin)) continue;
    if (module.navigationItems?.length) {
      moduleNavItems.push(
        ...module.navigationItems.map((item) => {
          const href = item.href ?? '';
          const fallback = item.labelFallback ?? module.name;
          return {
            ...item,
            label: href ? (
              <ModuleNavLabel
                namespace={module.id}
                fallback={fallback}
                href={href}
                isMocked={item.isMocked}
              />
            ) : (
              item.label
            )
          };
        })
      );
    }
  }

  function injectModuleItems(items: NavigationItem[]): NavigationItem[] {
    return items.map((item) => {
      if (item.key === moduleSlotKey && item.type === 'group') {
        const originalChildren = item.children ?? [];
        return { ...item, children: [...originalChildren, ...moduleNavItems] };
      }
      if (item.children) {
        return { ...item, children: injectModuleItems(item.children) };
      }
      return item;
    });
  }

  return injectModuleItems(filteredStatic);
}

/**
 * navigationConfig
 * 
 * Utility map used for configuring sidebar widths and titles per user area/context.
 * (E.g. controls sidebar expansion/collapse sizes and branding.)
 * 
 * Each key matches a context: 'admin', 'manage', or 'client'
 * - `titleKey`: i18n translation key for the area title (in the 'navigation' namespace)
 * - `expandedWidth`/`collapsedWidth`: Sidebar px width values
 */
export const navigationConfig = {
  admin: {
    titleKey: 'navigation:admin.title',
    title: 'admin', // fallback
    expandedWidth: 300,
    collapsedWidth: 80
  },
  manage: {
    titleKey: 'navigation:manage.title',
    title: 'manager', // fallback
    expandedWidth: 300,
    collapsedWidth: 80
  },
  client: {
    titleKey: 'navigation:client.title',
    title: 'client', // fallback
    expandedWidth: 240,
    collapsedWidth: 88
  }
} as const;
